import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressHeader from '../../components/ui/ProgressHeader';
import PerformanceMetrics from './components/PerformanceMetrics';
import QuestionReview from './components/QuestionReview';
import ProgressSummary from './components/ProgressSummary';
import ActionButtons from './components/ActionButtons';
import PerformanceComparison from './components/PerformanceComparison';


const QuizResultsPerformanceAnalytics = () => {
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Mock quiz results data
  const quizResults = {
    score: 85,
    totalQuestions: 10,
    correctAnswers: 8,
    timeSpent: 420, // seconds
    passingScore: 70,
    currentChapter: 2,
    performanceLevel: "Good",
    completedAt: new Date().toISOString(),
    currentScore: {
      score: 8,
      totalQuestions: 10,
      timeSpent: 420, // seconds
      completedAt: new Date().toISOString()
    },
    previousAttempts: [
      {
        score: 6,
        totalQuestions: 10,
        date: new Date(Date.now() - 86400000 * 2).toISOString(), // 2 days ago
        timeSpent: 380
      },
      {
        score: 7,
        totalQuestions: 10,
        date: new Date(Date.now() - 86400000 * 1).toISOString(), // 1 day ago
        timeSpent: 450
      }
    ],
    personalBest: {
      percentage: 80,
      score: 8,
      totalQuestions: 10,
      date: new Date().toISOString()
    },
  };

  const questions = [
    {
      id: 1,
      question: "What is the primary purpose of machine learning?",
      options: [
        "To replace human intelligence completely",
        "To enable computers to learn and improve from experience",
        "To create robots that look like humans",
        "To store large amounts of data"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Machine learning enables computers to learn patterns from data and improve their performance on tasks without being explicitly programmed for every scenario."
    },
    {
      id: 2,
      question: "Which of the following is NOT a type of machine learning?",
      options: [
        "Supervised Learning",
        "Unsupervised Learning",
        "Reinforcement Learning",
        "Quantum Learning"
      ],
      userAnswer: 2,
      correctAnswer: 3,
      explanation: "Quantum Learning is not a recognized type of machine learning. The three main types are Supervised, Unsupervised, and Reinforcement Learning."
    },
    {
      id: 3,
      question: "What does AI stand for?",
      options: [
        "Automated Intelligence",
        "Artificial Intelligence",
        "Advanced Integration",
        "Algorithmic Implementation"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "AI stands for Artificial Intelligence, which refers to the simulation of human intelligence in machines."
    },
    {
      id: 4,
      question: "Which company developed ChatGPT?",
      options: [
        "Google",
        "Microsoft",
        "OpenAI",
        "Meta"
      ],
      userAnswer: 2,
      correctAnswer: 2,
      explanation: "ChatGPT was developed by OpenAI, an AI research company founded in 2015."
    },
    {
      id: 5,
      question: "What is a neural network inspired by?",
      options: [
        "Computer circuits",
        "The human brain",
        "Mathematical equations",
        "Database structures"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Neural networks are inspired by the structure and function of biological neural networks in the human brain."
    },
    {
      id: 6,
      question: "What is deep learning?",
      options: [
        "Learning at great depths underwater",
        "A subset of machine learning using neural networks with multiple layers",
        "Learning for extended periods of time",
        "A type of database management"
      ],
      userAnswer: 0,
      correctAnswer: 1,
      explanation: "Deep learning is a subset of machine learning that uses neural networks with multiple layers (deep neural networks) to model and understand complex patterns in data."
    },
    {
      id: 7,
      question: "What is the main advantage of supervised learning?",
      options: [
        "It doesn't require labeled data",
        "It can learn from labeled examples to make predictions",
        "It works without any training data",
        "It only works with images"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Supervised learning uses labeled training data to learn patterns and make predictions on new, unseen data."
    },
    {
      id: 8,
      question: "What is natural language processing (NLP)?",
      options: [
        "Processing natural resources",
        "A field of AI that helps computers understand human language",
        "Learning natural sciences",
        "Processing language naturally without computers"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Natural Language Processing (NLP) is a branch of AI that focuses on the interaction between computers and human language, enabling machines to understand, interpret, and generate human language."
    },
    {
      id: 9,
      question: "What is overfitting in machine learning?",
      options: [
        "When a model is too simple",
        "When a model performs well on training data but poorly on new data",
        "When a model is too fast",
        "When a model uses too little data"
      ],
      userAnswer: 0,
      correctAnswer: 1,
      explanation: "Overfitting occurs when a machine learning model learns the training data too well, including noise and outliers, resulting in poor performance on new, unseen data."
    },
    {
      id: 10,
      question: "What is the purpose of training data in machine learning?",
      options: [
        "To test the final model",
        "To teach the model patterns and relationships",
        "To store the model",
        "To display results"
      ],
      userAnswer: 1,
      correctAnswer: 1,
      explanation: "Training data is used to teach machine learning models patterns and relationships so they can make accurate predictions on new, unseen data."
    }
  ];

  const performanceData = [
    { chapter: 'Ch 1', score: 92, timeSpent: 180, attempts: 1 },
    { chapter: 'Ch 2', score: 85, timeSpent: 240, attempts: 2 },
    { chapter: 'Ch 3', score: 78, timeSpent: 300, attempts: 1 },
    { chapter: 'Ch 4', score: 88, timeSpent: 220, attempts: 1 },
    { chapter: 'Ch 5', score: 95, timeSpent: 200, attempts: 1 }
  ];

  const topicPerformance = [
    { topic: 'AI Basics', score: 90, questions: 5 },
    { topic: 'Machine Learning', score: 80, questions: 4 },
    { topic: 'Neural Networks', score: 75, questions: 3 },
    { topic: 'Deep Learning', score: 85, questions: 4 },
    { topic: 'Applications', score: 95, questions: 4 }
  ];

  const completedChapters = [
    { id: 1, title: "Introduction to AI", score: 92, completed: true },
    { id: 2, title: "Machine Learning Basics", score: 85, completed: true }
  ];

  const nextChapter = {
    id: 3,
    title: "Neural Networks",
    unlocked: quizResults.score >= quizResults.passingScore,
    estimatedTime: "45 minutes"
  };

  const achievements = [
    { id: 1, title: "First Quiz Completed", icon: "Award", earned: true },
    { id: 2, title: "Perfect Score", icon: "Trophy", earned: quizResults.score === 100 },
    { id: 3, title: "Speed Learner", icon: "Zap", earned: quizResults.timeSpent < 300 },
    { id: 4, title: "Consistent Performer", icon: "Target", earned: quizResults.score >= 80 }
  ];

  // Check localStorage for saved language preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleRetakeQuiz = () => {
    console.log('Retaking quiz...');
    // This would typically reset quiz state and navigate to quiz interface
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20 pb-8">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Page Header */}
            <div className="space-y-4 text-center">
              <h1 className="text-4xl font-bold font-heading text-text-primary">
                Quiz Results & Performance Analytics
              </h1>
              <p className="max-w-3xl mx-auto text-lg text-text-secondary">
                Review your performance, understand your strengths and areas for improvement,
                and track your learning progress with detailed analytics.
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Left Column - Results & Review */}
              <div className="space-y-8 lg:col-span-2">
                <PerformanceMetrics
                  score={quizResults.score}
                  totalQuestions={quizResults.totalQuestions}
                  correctAnswers={quizResults.correctAnswers}
                  timeSpent={quizResults.timeSpent}
                  passingScore={quizResults.passingScore}
                  performanceLevel={quizResults.performanceLevel}
                />

                <QuestionReview questions={questions} />

                {/* <PerformanceChart
                  performanceData={performanceData}
                  topicPerformance={topicPerformance}
                /> */}

                <PerformanceComparison
                  currentScore={quizResults.currentScore}
                  previousAttempts={quizResults.previousAttempts}
                  personalBest={quizResults.personalBest}
                />
              </div>

              {/* Right Column - Progress & Actions */}
              <div className="space-y-8">
                <ActionButtons
                  score={quizResults.score}
                  passingScore={quizResults.passingScore}
                  canRetake={true}
                  nextChapterUnlocked={nextChapter.unlocked}
                  currentChapter={quizResults.currentChapter}
                  onRetakeQuiz={handleRetakeQuiz}
                  showDetailedAnalytics={true}
                />

                <ProgressSummary
                  currentChapter={quizResults.currentChapter}
                  totalChapters={8}
                  overallProgress={35}
                  completedChapters={completedChapters}
                  nextChapter={nextChapter}
                  achievements={achievements}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuizResultsPerformanceAnalytics;